import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Loader2 } from "lucide-react";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Logout control with a confirm-intent step (Round 2, Items 12 / 16b).
 * Click -> inline "Log out of your account?" confirm -> Yes runs logout and
 * lands on the public home. Reused on the My Purchases page, the clipper
 * dashboard and as the last option in subscriber profile settings.
 */
export const LogoutControl = ({ testid = "logout-control", label, full = false }) => {
  const { logout } = usePlatformAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const resolvedLabel = label ?? t("nav.logout");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const doLogout = async () => {
    setBusy(true);
    try { await logout(); navigate("/"); }
    finally { setBusy(false); }
  };

  if (confirming) {
    return (
      <div data-testid={`${testid}-confirm`} className="flex items-center gap-2 flex-wrap">
        <span className="text-sm" style={{ color: "var(--text-secondary)" }}>Log out of your account?</span>
        <button onClick={doLogout} disabled={busy} data-testid={`${testid}-confirm-yes`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold"
          style={{ background: "var(--error, #dc2626)", color: "#fff", opacity: busy ? 0.7 : 1 }}>
          {busy && <Loader2 size={14} className="animate-spin" />} Yes, log out
        </button>
        <button onClick={() => setConfirming(false)} disabled={busy} data-testid={`${testid}-confirm-cancel`}
          className="px-4 py-2 rounded-full text-sm font-medium"
          style={{ background: "var(--bg-subtle)", color: "var(--text-secondary)" }}>Cancel</button>
      </div>
    );
  }

  return (
    <button onClick={() => setConfirming(true)} data-testid={testid}
      className={`inline-flex items-center gap-2 text-sm font-semibold ${full ? "w-full justify-center py-3 rounded-full" : ""}`}
      style={full
        ? { background: "var(--bg-subtle)", color: "var(--error, #dc2626)", border: "1px solid var(--border-color)" }
        : { color: "var(--error, #dc2626)" }}>
      <LogOut size={16} /> {resolvedLabel}
    </button>
  );
};

export default LogoutControl;
